import { cloneDeep } from 'lodash';
import Vue from 'vue';

import SystemApi from 'client/api/v2/SystemApi';
import SystemUserApi from 'client/api/v2/SystemUserApi';

import { ItemTypeConsts } from 'client/util/Constants';
import DynamicGroupsProvideMap from 'client/groups/DynamicGroups/DynamicGroupsProvideMap';
import DynamicGroupsUtil from 'client/util/DynamicGroupsUtil';
import membershipMethodKeys, { EXEMPTION_TYPES } from 'client/groups/Components/constants/MembershipConstants';

const { DYNAMIC_AUTOMATED, DYNAMIC_REVIEW_REQUIRED, STATIC } = membershipMethodKeys;

const getNewFilter = () => ({
  id: '',
  attribute: '',
  operator: '',
  filterValues: [],
});

// ---------------------
// ----- State

const getDefaultDynamicGroupItem = () => ({
  memberQuery: {
    filters: [],
    queryType: 'FilterQuery',
  },
  memberQueryUI: {
    filters: [],
    idIncrement: 0,
  },
  memberQueryExemptions: [],
  membershipMethod: STATIC,
  memberSuggestionsNotify: false,
});

const getDefaultDynamicGroupState = () => ({
  isPendingReview: false,
});

/**
 * Creates a unique ID string (for the purposes of input validation)
 * @param {Object} obj
 * @param {string} obj.type tells if we're creating an id for a filter, or a filter value
 * @param {number} obj.idIncrement current idIncrement in state
 * @returns {string} generated ID string
 */
const getIdString = ({ type, idIncrement }) => `${type}-${idIncrement}`;

/**
 * Validation system
 * Start with increment 0
 *
 * When translating GET API response body to `memberQueryUI` format,
 * need to use increment to generate id's for existing filters/values
 *
 * When user adds filter/value, use same increment from before and
 * increase by 1
 *
 * On Aside close, reset increment.
 *
 * filters
 * `filter-${idIncrement}`
 * e.g. filter-2

 * filter values
 * `filterValue-${idIncrement}`
 * e.g. filterValue-5
 *
 * Using same increment for filters & filter values ensures the same
 * increment number can't be used for a filter and a filter value at the same
 * time
 */

// ---------------------
// ----- Getters

const getIsPendingReview = (state) => state.isPendingReview;

const exemptionsSortFn = (a, b) => a.name.localeCompare(b.name);

/** Getter for Rule Builder `filters`. */
/** @returns {{id: string, attribute: string, operator: string, filterValues: []}[]} */
const getMembershipFilters = (state) => state.item.memberQueryUI.filters;

const getFilterByIndex = (state) => (index) => state.item.memberQueryUI.filters[index];

const getMembershipMethod = (state) => state.item.membershipMethod;

const getMemberSuggestionsNotify = (state) => state.item.memberSuggestionsNotify;

const getHasMembershipChanged = (_, getters) => {
  const { getHasMembershipMethodChanged, getHaveMembershipFiltersChanged } = getters;

  return getHasMembershipMethodChanged || getHaveMembershipFiltersChanged;
};

const getHasMembershipMethodChanged = (_, getters) => {
  const { item, previousItem } = getters;

  const { membershipMethod } = item;
  const { membershipMethod: previousMembershipMethod } = previousItem;

  return membershipMethod !== previousMembershipMethod;
};

const getHaveMembershipFiltersChanged = (_, getters) => {
  const { item, previousItem } = getters;

  const { memberQueryUI } = item;
  const { memberQueryUI: previousMemberQueryUI } = previousItem;

  return JSON.stringify(memberQueryUI) !== JSON.stringify(previousMemberQueryUI);
};

const getMemberQueryUI = (state) => state.item.memberQueryUI;

/** @returns {{ id: string, type: string, name?: string, op?: string }[]} */
const getMembershipExemptions = (state) => {
  let exemptions = state.item.memberQueryExemptions;

  /**
   * Initially, exemptions will only have { id: string, type: string } before we
   * map the items' names in.
   *
   * So, we check to see if the field we need exists before allowing them
   * to be sorted so we don't cause an error.
   */
  const namesHaveBeenMapped = exemptions.length && exemptions[0].name != null;
  if (namesHaveBeenMapped) {
    exemptions = exemptions.toSorted(exemptionsSortFn);
  }

  return exemptions;
};

const getMembershipExemptionsIds = (state) => state.item.memberQueryExemptions.map(({ id }) => id);

const getMembershipExclusions = (_, getters) => getters.getMembershipExemptions
  .filter(({ op }) => op === 'exclude');

const getMembershipInclusions = (_, getters) => getters.getMembershipExemptions
  .filter(({ op }) => op === 'include');

const getPreviousMembershipExemptions = (state) => state.previousItem.memberQueryExemptions;

const getHaveExemptionsChanged = (_, getters) => JSON.stringify(getters.getMembershipExemptions)
  !== JSON.stringify(getters.getPreviousMembershipExemptions);

// ---------------------
// ----- Actions

const setIsPendingReview = ({ commit }, isPendingReview) => commit('SET_IS_PENDING_REVIEW', isPendingReview);

// Rule Builder
const addFilter = ({ commit, getters }, payload) => {
  const { shouldAddFilterValue = true } = payload;

  commit('ADD_FILTER', payload);
  commit('INCREMENT_ID');

  if (shouldAddFilterValue) {
    const filters = getters.getMembershipFilters;
    const lastFilterIndex = filters.length - 1;

    commit('ADD_FILTER_VALUE', { filterIndex: lastFilterIndex, value: '' });
    commit('INCREMENT_ID');
  }
};

const updateFilterAttribute = ({ commit, dispatch, getters }, payload) => {
  const { defaultFilterValue, filterIndex, newAttribute } = payload;

  const currentAttribute = getters.getFilterByIndex(filterIndex).attribute;
  if (currentAttribute === newAttribute) return;

  commit('UPDATE_ATTRIBUTE', { filterIndex, newAttribute });
  commit('RESET_FILTER_VALUES', { filterIndex });

  dispatch('addFilterValue', { filterIndex, value: defaultFilterValue });
};

const updateFilterOperator = ({ commit, getters }, payload) => {
  const { filterIndex, newOperator } = payload;

  const currentOperator = getters.getFilterByIndex(filterIndex).operator;
  if (currentOperator === newOperator) return;

  commit('UPDATE_OPERATOR', payload);

  // if 'not equals' (ne) is selected as the new operator, remove all filter values
  // except the first one
  if (newOperator === 'ne') {
    commit('RESET_FILTER_VALUES_EXCEPT_FIRST', { filterIndex });
  }
};

const addFilterValue = ({ commit }, payload) => {
  commit('ADD_FILTER_VALUE', payload);
  commit('INCREMENT_ID');
};

const updateFilterValue = ({ commit }, payload) => { commit('UPDATE_FILTER_VALUE', payload); };
const removeFilterValue = ({ commit }, payload) => { commit('REMOVE_FILTER_VALUE', payload); };

// Membership Method
const updateMembershipMethod = ({ commit }, method) => {
  commit('UPDATE_MEMBERSHIP_METHOD', method);
};
const updateMemberSuggestionsNotify = ({ commit }, payload) => { commit('UPDATE_MEMBER_SUGGESTIONS_NOTIFY', payload); };

// Exemptions
const addMemberQueryExemption = ({ commit }, payload) => { commit('ADD_MEMBER_QUERY_EXEMPTION', payload); };
const removeMemberQueryExemption = ({ commit }, payload) => { commit('REMOVE_MEMBER_QUERY_EXEMPTION', payload); };
const updateMemberQueryExemptions = ({ commit }, payload) => { commit('UPDATE_MEMBER_QUERY_EXEMPTIONS', payload); };
const updateExemptionOperation = ({ commit }, payload) => { commit('UPDATE_EXEMPTION_OPERATION', payload); };

const syncExemptionWithItemSelection = (
  { commit, getters, rootGetters },
  { id, checked, groupType },
) => {
  const {
    getMembershipExemptionsIds: exemptionsIds,
    getPreviousMembershipExemptions: previousExemptions,
    getMembershipMethod: membershipMethod,
  } = getters;

  if (membershipMethod === STATIC) return;

  const { itemCollectionName, itemNameField } = DynamicGroupsProvideMap[groupType];

  const getItem = rootGetters[`${itemCollectionName}/getItem`];
  const isItemPreviouslySelected = rootGetters[`${itemCollectionName}/isItemPreviouslySelected`];

  const exemptionIndex = exemptionsIds.findIndex((exemptionId) => id === exemptionId);
  const hasExemption = exemptionIndex > -1;
  const isPreviouslySelected = isItemPreviouslySelected(id);

  /**
   * Determines if this item has an exemption that has been created
   * during this aside "session", but not saved yet.
   */
  const isUnsavedExemption = !previousExemptions.find(({ id: exemptionId }) => id === exemptionId);

  /**
   * Determines if this item's new "selected" status has been set equal
   * to its original selection status
   */
  const boundStatusReset = checked === isPreviouslySelected;
  const op = checked ? 'include' : 'exclude';

  if (membershipMethod === DYNAMIC_AUTOMATED) {
    // if no exemption exists for this item, create one
    if (!hasExemption) {
      commit('ADD_MEMBER_QUERY_EXEMPTION', {
        id,
        op,
        name: getItem(id)[itemNameField],
        type: EXEMPTION_TYPES[groupType],
      });
    } else if (isUnsavedExemption && boundStatusReset) {
      /**
       * If it's a newly selected exemption, and the user reverses the binding
       * to its original state, we want to remove that exemption.
       */
      commit('REMOVE_MEMBER_QUERY_EXEMPTION', { index: exemptionIndex });
    } else {
      /**
       * If it's a pre-existing exemption and the binding status has changed,
       * change the type of exemption, but keep it.
       */
      commit('UPDATE_EXEMPTION_OPERATION', { index: exemptionIndex, op });
    }
  } else if (membershipMethod === DYNAMIC_REVIEW_REQUIRED && hasExemption && isUnsavedExemption) {
    commit('UPDATE_EXEMPTION_OPERATION', { index: exemptionIndex, op });
  }
};

/**
 * Initial setup for Exemptions. First we grab the previous exemptions from state,
 * get the bound status of those IDs, and map them to the relevant exemption type:
 *
 * Exemption BOUND -> Inclusion
 * Exemption NOT BOUND -> Exclusion
 *
 * After that, we call off for the display names of the exemptions, set them in
 * the id-name map, and set the local data Sets for inclusions & exclusions.
 */
const mapMembershipExemptions = async (
  {
    commit,
    dispatch,
    getters,
    rootState,
  },
  groupType = ItemTypeConsts.systemGroup,
) => {
  const provide = DynamicGroupsProvideMap[groupType];
  const {
    getMembershipExemptionsIds: exemptionsIds,
    getMembershipExemptions: exemptions,
    id: groupId,
  } = getters;

  if (!groupId || !exemptionsIds.length) return;

  const { features } = rootState[provide.itemCollectionName];

  if (!features.boundItemsUrl) {
    const boundItemsUrl = provide.getBoundItemsUrl(groupId);

    await dispatch(
      `${provide.itemCollectionName}/setBoundItemsUrl`,
      { url: boundItemsUrl },
      { root: true },
    );
  }

  const itemApi = groupType === ItemTypeConsts.systemGroup
    ? SystemApi
    : SystemUserApi;

  await dispatch(`${provide.itemCollectionName}/fetchCollection`, {}, { root: true });
  const getItemsById = async (ids) => {
    const { results } = await itemApi.filter({
      fields: [provide.itemNameField],
      filter: { or: ids.map(id => ({ _id: id })) },
      limit: 25,
    });

    return results;
  };
  const promises = [
    dispatch(`${provide.itemCollectionName}/checkForBoundItemsByIds`, exemptionsIds, { root: true }),
    getItemsById(exemptionsIds),
  ];

  const [{ boundItemIds }, exemptionsNamesResponse] = await Promise.all(promises);

  const operationMappedExemptions = exemptions.map(exemption => {
    const item = exemptionsNamesResponse.find(({ _id }) => _id === exemption.id);

    return {
      ...exemption,
      name: item[provide.itemNameField],
      op: boundItemIds.includes(exemption.id)
        ? 'include'
        : 'exclude',
    };
  });

  commit('UPDATE_MEMBER_QUERY_EXEMPTIONS', { newValue: operationMappedExemptions });
  commit('SET_PREVIOUS_ITEM_FIELD', {
    key: 'memberQueryExemptions',
    value: cloneDeep(operationMappedExemptions),
  });
};

const setMemberQuery = ({ commit }) => { commit('SET_MEMBER_QUERY'); };

const resetIdIncrement = ({ commit }) => { commit('RESET_ID_INCREMENT'); };

/**
 * RegExp to match on all pipes (|) without the back slashes (\\) in front,
 * so we can get all filter values from current `memberQuery.filters[x].value`
 *
 * Also, since `User Groups` can have empty string conditions, we need to match them
 * in order to display the filter values with its attribute, operator and empty input.
 */
const newFilterValueSeparationRegex = /(?:\\.|[^|\\]+|^$)+/gim;

const transformMemberQueryToUI = (dispatch, { memberQuery }) => {
  const { filters = [] } = memberQuery || {};

  filters.forEach((filter, index) => {
    const { field, operator, value } = filter;

    // create filter
    dispatch('addFilter', {
      attributeValue: field,
      operatorValue: operator === 'in' ? 'eq' : operator,
      shouldAddFilterValue: false,
    });

    /** @type {IterableIterator<RegExpMatchArray>} */
    const matches = value.match(newFilterValueSeparationRegex) || [];

    matches.forEach(match => {
      // remove slashes from value (since we know that `match` is a whole value)
      // (i.e. will take up an entire value input in Rule Builder)
      const valueSlashesRemoved = match.replaceAll(/\\{1}/g, '');

      dispatch('addFilterValue', {
        filterIndex: index,
        value: valueSlashesRemoved,
      });
    });
  });
};

/**
 * Sets all the membership data.
 * @param {object} context Vuex context object
 */
const setMemberQueryUI = ({ dispatch, state }) => {
  const { item } = state;

  // Reset memberQueryUI to prevent accumulation.
  dispatch('resetMemberQueryUI');

  transformMemberQueryToUI(dispatch, item);

  dispatch('setPreviousItemMemberQueryUI');
};

const resetMemberQueryUI = ({ commit }) => { commit('RESET_MEMBER_QUERY_UI'); };

const setPreviousItemMemberQueryUI = ({ commit }) => { commit('SET_PREVIOUS_ITEM_MEMBER_QUERY_UI'); };

const resetMembershipData = ({ commit }) => { commit('RESET_MEMBERSHIP_DATA'); };

// ---------------------
// ----- Mutations

const SET_IS_PENDING_REVIEW = (state, isPendingReview) => Vue.set(state, 'isPendingReview', isPendingReview);

// Rule Builder
const ADD_FILTER = (state = {}, { attributeValue, operatorValue }) => {
  const newFilter = getNewFilter();
  newFilter.attribute = attributeValue;
  newFilter.operator = operatorValue;
  newFilter.id = getIdString({
    type: 'filter',
    idIncrement: state.item.memberQueryUI.idIncrement,
  });

  state.item.memberQueryUI.filters.push(newFilter);
};

const UPDATE_ATTRIBUTE = (state = {}, { filterIndex, newAttribute }) => {
  Vue.set(state.item.memberQueryUI.filters[filterIndex], 'attribute', newAttribute);
};

const UPDATE_OPERATOR = (state = {}, { filterIndex, newOperator }) => {
  Vue.set(state.item.memberQueryUI.filters[filterIndex], 'operator', newOperator);
};

// optionally takes in `value` so we can use this mutation while initially
// transforming the API call data to the `memberQueryUI` format
const ADD_FILTER_VALUE = (state = {}, { filterIndex, value = '' }) => {
  const newFilterValue = { id: '', value };

  newFilterValue.id = getIdString({
    type: 'filterValue',
    idIncrement: state.item.memberQueryUI.idIncrement,
  });

  state.item.memberQueryUI.filters[filterIndex].filterValues.push(newFilterValue);
};

const UPDATE_FILTER_VALUE = (state = {}, { filterIndex, filterValueIndex, newValue }) => {
  Vue.set(
    state.item.memberQueryUI.filters[filterIndex].filterValues[filterValueIndex],
    'value',
    newValue,
  );
};

/**
 * Removes just the filter value if there are more filter values than the one
 * being removed.
 *
 * Otherwise, remove the whole filter.
 */
const REMOVE_FILTER_VALUE = (state = {}, { filterIndex, filterValueIndex }) => {
  if (state.item.memberQueryUI.filters[filterIndex].filterValues.length > 1) {
    state.item.memberQueryUI.filters[filterIndex].filterValues.splice(filterValueIndex, 1);
  } else {
    // when removing the last filter value, remove the filter entirely
    state.item.memberQueryUI.filters.splice(filterIndex, 1);
  }
};

const RESET_FILTER_VALUES = (state = {}, { filterIndex }) => {
  Vue.set(state.item.memberQueryUI.filters[filterIndex], 'filterValues', []);
};

const RESET_FILTER_VALUES_EXCEPT_FIRST = (state = {}, { filterIndex }) => {
  state.item.memberQueryUI.filters[filterIndex].filterValues.splice(1);
};

const RESET_MEMBER_QUERY_UI = (state = {}) => {
  const defaultMembershipState = getDefaultDynamicGroupItem();

  Vue.set(state.item, 'memberQueryUI', defaultMembershipState.memberQueryUI);
};

const RESET_MEMBERSHIP_DATA = (state = {}) => {
  const defaultMembershipState = getDefaultDynamicGroupItem();

  Vue.set(state.item, 'memberQuery', defaultMembershipState.memberQuery);
  Vue.set(state.item, 'memberQueryUI', defaultMembershipState.memberQueryUI);
  Vue.set(state.item, 'memberQueryExemptions', defaultMembershipState.memberQueryExemptions);
  Vue.set(state.item, 'membershipMethod', defaultMembershipState.membershipMethod);
  Vue.set(state.item, 'memberSuggestionsNotify', defaultMembershipState.memberSuggestionsNotify);
};

const SET_PREVIOUS_ITEM_MEMBER_QUERY_UI = (state = {}) => {
  Vue.set(state.previousItem, 'memberQueryUI', JSON.parse(JSON.stringify(state.item.memberQueryUI)));
};

const transformMemberQueryFiltersForAPI = (state = {}) => {
  const { filters } = state.item.memberQueryUI;

  const memberQueryFilters = filters.map(filter => {
    const { attribute } = filter;

    const joinedFilterValues = DynamicGroupsUtil.joinFilterValues(filter);
    const translatedOperator = DynamicGroupsUtil.translateOperator(filter);

    return {
      field: attribute,
      operator: translatedOperator,
      value: joinedFilterValues,
    };
  });

  return memberQueryFilters;
};

const SET_MEMBER_QUERY = (state = {}) => {
  const memberQueryFilters = transformMemberQueryFiltersForAPI(state);

  if (!state.item.memberQuery) {
    /**
     * To prevent an exception, the entire `memberQuery` object needs to be initialized.
     */
    Vue.set(state.item, 'memberQuery', getDefaultDynamicGroupItem().memberQuery);
  }

  const memberQueryFiltersEmpty = memberQueryFilters.length === 1
    && !memberQueryFilters[0].field;

  if (!memberQueryFiltersEmpty) {
    Vue.set(state.item.memberQuery, 'filters', memberQueryFilters);
  }
};

// Membership Type
const UPDATE_MEMBERSHIP_METHOD = (state = {}, method) => {
  Vue.set(state.item, 'membershipMethod', method);
};

// @todo: just pass the value
const UPDATE_MEMBER_SUGGESTIONS_NOTIFY = (state = {}, { newValue }) => {
  Vue.set(state.item, 'memberSuggestionsNotify', newValue);
};

// Exemptions
// @todo: just pass the value
const ADD_MEMBER_QUERY_EXEMPTION = (state, payload) => {
  state.item.memberQueryExemptions.push(payload);
};

const REMOVE_MEMBER_QUERY_EXEMPTION = (state = {}, { index }) => {
  state.item.memberQueryExemptions.splice(index, 1);
};

const UPDATE_MEMBER_QUERY_EXEMPTIONS = (state = {}, { newValue }) => {
  Vue.set(state.item, 'memberQueryExemptions', newValue);
};

const UPDATE_EXEMPTION_OPERATION = (state, { index, op }) => {
  const newExemption = state.item.memberQueryExemptions[index];

  Vue.set(newExemption, 'op', op);

  state.item.memberQueryExemptions.splice(index, 1, newExemption);
};

// internal

const INCREMENT_ID = (state = {}) => {
  Vue.set(
    state.item.memberQueryUI,
    'idIncrement',
    state.item.memberQueryUI.idIncrement + 1,
  );
};

const RESET_ID_INCREMENT = (state = {}) => {
  Vue.set(
    state.item.memberQueryUI,
    'idIncrement',
    0,
  );
};

/** ---------------
 * ----- Exports
 * ----------------
 * actions/mutations grouped by position of related inputs on page:
 * 1) membership method & email notifications options
 * 2) add filter, update attribute/operator
 * 3) add filter value, update, remove
 * 4) membership exemptions
 */

export {
  // ----- STATE
  getDefaultDynamicGroupItem,
  getDefaultDynamicGroupState,

  // ----- GETTERS
  getIsPendingReview,
  getFilterByIndex,
  getHasMembershipChanged,
  getHasMembershipMethodChanged,
  getHaveMembershipFiltersChanged,
  getMembershipFilters,
  getMembershipMethod,
  getMemberSuggestionsNotify,
  getMembershipExemptions,
  getMembershipExemptionsIds,
  getMembershipExclusions,
  getMembershipInclusions,
  getPreviousMembershipExemptions,
  getHaveExemptionsChanged,
  getMemberQueryUI,

  // ----- ACTIONS
  setIsPendingReview,

  updateMembershipMethod,
  updateMemberSuggestionsNotify,

  addFilter,
  updateFilterAttribute,
  updateFilterOperator,

  addFilterValue,
  updateFilterValue,
  removeFilterValue,

  addMemberQueryExemption,
  removeMemberQueryExemption,
  updateMemberQueryExemptions,
  updateExemptionOperation,
  mapMembershipExemptions,
  syncExemptionWithItemSelection,

  resetIdIncrement,

  resetMemberQueryUI,

  setMemberQuery,
  setMemberQueryUI,
  resetMembershipData,

  setPreviousItemMemberQueryUI,

  // ----- MUTATIONS
  SET_IS_PENDING_REVIEW,

  UPDATE_MEMBERSHIP_METHOD,
  UPDATE_MEMBER_SUGGESTIONS_NOTIFY,

  ADD_FILTER,
  UPDATE_ATTRIBUTE,
  UPDATE_OPERATOR,

  ADD_FILTER_VALUE,

  UPDATE_FILTER_VALUE,
  REMOVE_FILTER_VALUE,
  RESET_FILTER_VALUES,
  RESET_FILTER_VALUES_EXCEPT_FIRST,

  ADD_MEMBER_QUERY_EXEMPTION,
  REMOVE_MEMBER_QUERY_EXEMPTION,
  UPDATE_MEMBER_QUERY_EXEMPTIONS,
  UPDATE_EXEMPTION_OPERATION,

  INCREMENT_ID,
  RESET_ID_INCREMENT,

  RESET_MEMBER_QUERY_UI,

  RESET_MEMBERSHIP_DATA,

  SET_MEMBER_QUERY,

  SET_PREVIOUS_ITEM_MEMBER_QUERY_UI,
};
